/**
 * WizardStepVariables - Passo 2: Revisar/editar variáveis pré-preenchidas
 *
 * Utiliza o parser central contractVariableParser para interpretar a sintaxe
 * expandida de variáveis com modificadores:
 *   - text     → <Input> (com asterisco vermelho se required)
 *   - select   → <Select> dropdown com opções do template
 *   - multiselect → grupo de <Checkbox>, valor salvo como CSV
 *
 * Produtos são exibidos em tabela editável via WizardProductsTable
 *
 * RELEASE: v1.2.0 - 2026-02-19
 * - Suporte a meta-variáveis: required (*), defaultValue (||), select ([,]), multiselect ([;])
 */
import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Separator } from '@/components/ui/separator';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import type { ContractTemplate, WizardDealItem } from '@/types/contracts';
import { PREDEFINED_VARIABLES } from '@/types/contracts';
import { parseContractVariables } from '@/lib/contractVariableParser';
import { WizardProductsTable } from './WizardProductsTable';

// Conjunto de chaves marcadas como "Totais do Negócio" (subgrupo do source 'deal')
const TOTALS_KEYS = new Set(
  PREDEFINED_VARIABLES.filter((v) => v.group === 'totals').map((v) => v.key),
);

// Props do componente
interface WizardStepVariablesProps {
  template: ContractTemplate;
  variables: Record<string, string>;
  onChangeVariables: (vars: Record<string, string>) => void;
  /** Itens de produto do deal para tabela editável */
  dealItems: WizardDealItem[];
  /** Callback ao alterar itens de produto */
  onChangeDealItems: (items: WizardDealItem[]) => void;
}

export function WizardStepVariables({
  template,
  variables,
  onChangeVariables,
  dealItems,
  onChangeDealItems,
}: WizardStepVariablesProps) {
  // Verifica se template usa bloco de loop de produtos
  const hasProductLoop = /\{\{#produtos\}\}/.test(template.content_html);

  // Parseia todas as variáveis do template usando o parser central
  const parsedVars = parseContractVariables(template.content_html);

  // Filtra variáveis que devem aparecer como campos (exclui produtos_servicos se há loop)
  const simpleVars = parsedVars.filter((v) => {
    if (v.key === 'produtos_servicos' && hasProductLoop) return false;
    return true;
  });

  /** Atualiza o valor de uma variável no mapa */
  const handleChange = (key: string, value: string) => {
    onChangeVariables({ ...variables, [key]: value });
  };

  /**
   * Trata seleção/desseleção de opção em campo multiselect.
   * O valor é armazenado como CSV das opções marcadas.
   */
  const handleMultiselectChange = (key: string, option: string, checked: boolean) => {
    const currentValues = variables[key]
      ? variables[key].split(',').map((v) => v.trim()).filter(Boolean)
      : [];

    const newValues = checked
      ? [...currentValues, option]
      : currentValues.filter((v) => v !== option);

    handleChange(key, newValues.join(', '));
  };

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Revise e edite as variáveis que serão preenchidas no contrato.
        Valores pré-preenchidos foram obtidos dos dados do negócio e contato.
      </p>

      {/* Renderiza cada variável de acordo com seu tipo */}
      {simpleVars.length > 0 && (
        <div className="grid gap-4">
          {simpleVars.map((v) => {
            const currentValue = variables[v.key] ?? v.defaultValue ?? '';

            return (
              <div key={v.key} className="grid gap-1.5">
                {/* Label com indicador de obrigatório */}
                <Label className="text-xs flex items-center gap-1">
                  {v.label}
                  {v.required && (
                    <span className="text-destructive font-bold" title="Campo obrigatório">*</span>
                  )}
                  <span className="text-muted-foreground font-normal">{`{{${v.key}}}`}</span>
                </Label>

                {/* Campo de texto simples */}
                {v.type === 'text' && (
                  <Input
                    value={currentValue}
                    onChange={(e) => handleChange(v.key, e.target.value)}
                    placeholder={v.defaultValue || v.label}
                    className={`h-9 text-sm ${v.required && !currentValue.trim() ? 'border-destructive/50' : ''}`}
                  />
                )}

                {/* Dropdown de seleção única */}
                {v.type === 'select' && (
                  <Select
                    value={currentValue}
                    onValueChange={(val) => handleChange(v.key, val === '__vazio__' ? '' : val)}
                  >
                    <SelectTrigger className={`h-9 text-sm ${v.required && !currentValue.trim() ? 'border-destructive/50' : ''}`}>
                      <SelectValue placeholder={`Selecionar ${v.label.toLowerCase()}`} />
                    </SelectTrigger>
                    <SelectContent>
                      {/* Opção vazia */}
                      {!v.required && (
                        <SelectItem value="__vazio__">
                          <span className="text-muted-foreground">(vazio)</span>
                        </SelectItem>
                      )}
                      {v.options?.map((opt) => (
                        <SelectItem key={opt} value={opt}>
                          {opt}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}

                {/* Checkboxes para seleção múltipla */}
                {v.type === 'multiselect' && (
                  <div className={`rounded-md border p-3 space-y-2 ${v.required && !currentValue.trim() ? 'border-destructive/50' : 'border-input'}`}>
                    {v.options?.map((opt) => {
                      const selectedValues = currentValue
                        ? currentValue.split(',').map((s) => s.trim())
                        : [];
                      const isChecked = selectedValues.includes(opt);

                      return (
                        <div key={opt} className="flex items-center gap-2">
                          <Checkbox
                            id={`${v.key}-${opt}`}
                            checked={isChecked}
                            onCheckedChange={(checked) =>
                              handleMultiselectChange(v.key, opt, !!checked)
                            }
                          />
                          <label
                            htmlFor={`${v.key}-${opt}`}
                            className="text-sm cursor-pointer select-none"
                          >
                            {opt}
                          </label>
                        </div>
                      );
                    })}
                    {currentValue && (
                      <p className="text-xs text-muted-foreground pt-1 border-t">
                        Selecionado: <span className="font-medium">{currentValue}</span>
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Tabela de produtos (quando template usa loop) */}
      {hasProductLoop && (
        <>
          <Separator />
          <WizardProductsTable
            items={dealItems}
            onChangeItems={onChangeDealItems}
          />
        </>
      )}

      {/* Mensagem quando não há variáveis nem produtos */}
      {simpleVars.length === 0 && !hasProductLoop && (
        <p className="text-sm text-muted-foreground py-4 text-center">
          Este modelo não possui variáveis dinâmicas.
        </p>
      )}
    </div>
  );
}
